import { useEffect } from "react";

import { Box, Button, Typography } from "@braintrust/braintrust-ui-components";
import { WarningModalIcon } from "@js/apps/common/components/warning-modal-icon";
import { Modal } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";

import { openPasswordConfirmationModal } from "../../passkey-auth/password-confirmation-modal";
import { useRegisterPasskey } from "../../passkey-auth/use-register-passkey";
import { openEnableOTPAuthModal } from "../enable-otp-modal";

import styles from "./styles.module.scss";

const OTP_DISABLED_WARNING_MODAL_ID = "otp-disabled-warning";

export const OTPDisabledWarningModalInstance = Modal(
  OTP_DISABLED_WARNING_MODAL_ID,
  {
    className: styles.modal,
    closeButton: false,
  },
);

type OTPDisabledWarningProps = {
  onSuccessAction?: (codes: string[]) => void;
  onPasskeySuccessAction?: () => void;
};

const OTPDisabledWarning = ({
  onSuccessAction,
  onPasskeySuccessAction,
}: OTPDisabledWarningProps) => {
  const { register, isRegistering } = useRegisterPasskey();

  useEffect(() => {
    return () => OTPDisabledWarningModalInstance.close();
  }, []);

  const handleSetUpPasskey = () => {
    OTPDisabledWarningModalInstance.close();
    openPasswordConfirmationModal({
      title: "Set up a passkey",
      description: "Enter your current password to set up a passkey.",
      confirmText: "Set up",
      onConfirm: async (currentPassword) => {
        await register({ current_password: currentPassword });
        Snackbar.success("Passkey added.");
        onPasskeySuccessAction?.();
      },
    });
  };

  return (
    <>
      <Typography
        component="h3"
        variant="title"
        fontWeight={400}
        size="small"
        className="mt0 mb+"
      >
        <WarningModalIcon /> 2-Step Authentication Required
      </Typography>
      <Typography component="p">
        For security purposes, you must enable 2-Step Authentication before
        taking this action.
      </Typography>
      <Box
        className="mt+"
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "flex-end",
          alignItems: "flex-end",
          flexDirection: { xs: "column-reverse", sm: "row" },
        }}
      >
        <Button
          variant="secondary"
          onClick={OTPDisabledWarningModalInstance.close}
        >
          Cancel
        </Button>
        <Button
          variant="secondary"
          disabled={isRegistering}
          onClick={handleSetUpPasskey}
        >
          {isRegistering ? "Setting up…" : "Set up a passkey"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            OTPDisabledWarningModalInstance.close();
            openEnableOTPAuthModal({ onSuccessAction });
          }}
        >
          Setup authenticator app
        </Button>
      </Box>
    </>
  );
};

export const openOTPDisabledWarningModal = (
  config?: OTPDisabledWarningProps,
) => {
  OTPDisabledWarningModalInstance.open({
    children: <OTPDisabledWarning {...config} />,
  });
};
